import type {
    PQueryBuilderField,
    PQueryFieldGroupDefinition,
} from '@pushly/aqe/lib/components/pquery-builder'
import type { ExternalEventProperty } from '@pushly/models/lib/structs/external-events/external-event-property'
import { ExternalEventPropertyType } from '@pushly/models/lib/enums/external-events/external-event-property-type'

/**
 * The prefix every event property is addressed by, in a condition and in a late-bound value alike.
 *
 * One namespace for both uses is what keeps the two halves of the feature describable in the same
 * words: a filter over the event's own properties names `event.properties.home_team`, and a
 * subscriber condition bound to that same property carries the identical path inside its `$event`
 * reference. The router substitutes by that path, so it is a wire fact rather than a display choice.
 */
export const EVENT_PROPERTY_NAMESPACE = 'event.properties.'

/**
 * The key a late-bound value refers to an event property by.
 *
 * The stored form is `{ "$event": "<path>" }` — the reference is the whole value, so a condition
 * holding one carries no literal at all and the router supplies the value at routing time.
 */
export const EVENT_REFERENCE_KEY = '$event'

/** A condition value that names an event property rather than holding a literal. */
export interface EventPropertyReference {
    $event: string
}

/**
 * True when a condition's value is a reference to an event property rather than a literal.
 *
 * Read before anything else touches a value: an editor that mistakes a reference for a literal
 * hands an object to a text control, which renders it as `[object Object]` and replaces it with a
 * string on the first keystroke — irreversibly, and without the save being blocked.
 */
export function isEventPropertyReference(value: unknown): value is EventPropertyReference {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
    return typeof (value as EventPropertyReference).$event === 'string'
}

/** The reference value that binds a condition to one event property. */
export function buildEventPropertyReference(propertyKey: string): EventPropertyReference {
    return { $event: propertyKey }
}

/**
 * The registered property types, expressed as the builder's field types.
 *
 * The registry deliberately types event properties from the same set segment fields use, so an
 * event property and a subscriber attribute compared against each other are offered the same
 * operators and the same editor. Keyed by every member so a type added to the registry stops the
 * build here rather than silently becoming text.
 */
const FIELD_TYPE_BY_PROPERTY_TYPE: Readonly<Record<ExternalEventPropertyType, PQueryBuilderField['type']>> = {
    [ExternalEventPropertyType.TEXT]: 'text',
    [ExternalEventPropertyType.NUMERIC]: 'integer',
    [ExternalEventPropertyType.BOOLEAN]: 'boolean',
    [ExternalEventPropertyType.DATE]: 'date',
    [ExternalEventPropertyType.ARRAY]: 'array',
}

/**
 * Whether a registered property's values carry a fractional part.
 *
 * The registry types every number `numeric` and records in `type_hint` whether it is a `float`.
 * Reading only the type would offer a whole-number control for a value like 12.5, which rounds
 * what is typed into it to 13. An unhinted number is a whole one, which is what the registry means
 * by it.
 */
function isFractionalNumber(property: ExternalEventProperty): boolean {
    return property.type === ExternalEventPropertyType.NUMERIC && property.type_hint === 'float'
}

/** The full property path a registered property is addressed by. */
export function eventPropertyPath(propertyKey: string): string {
    return `${EVENT_PROPERTY_NAMESPACE}${propertyKey}`
}

/**
 * The menu group event properties are listed under, and the heading it renders.
 *
 * Their own heading rather than a share of one, because an event property and a subscriber
 * attribute are not interchangeable and a picker that lists them together says they are. One is
 * stored on a subscriber and lives until it changes; the other exists for the length of one event
 * and is then gone, so a condition written against it means nothing outside the event that
 * triggered the journey.
 */
export const EVENT_PROPERTY_FIELD_GROUP = 'event'

export const EVENT_PROPERTY_FIELD_GROUPS: ReadonlyArray<PQueryFieldGroupDefinition> = [
    { group: EVENT_PROPERTY_FIELD_GROUP, label: 'Event Properties' },
]

/** Everything that is not a letter or a digit, which is what a word boundary in a key looks like. */
const KEY_SEPARATORS = /[^a-zA-Z0-9]+/

/**
 * A registered property key read as words.
 *
 * The property table holds only the dotted key a partner's payload is shaped by, so a picker
 * showing it raw offers `home_team` where every neighbouring field offers a name. Capitalising the
 * words the key already contains is the whole of the transformation: nothing is renamed, reordered
 * or dropped, so the reading stays recoverable from the key and cannot claim the property is called
 * something the partner never called it.
 */
export function humanizeEventPropertyKey(propertyKey: string): string {
    return propertyKey
        .split(KEY_SEPARATORS)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
}

/** A key stripped to what a reading of it has to preserve, for deciding whether it still shows. */
const asComparableKey = (value: string) => value.replace(/[^a-zA-Z0-9]+/g, '').toLowerCase()

/**
 * How one registered property is named in a picker.
 *
 * A registered display name wins where there is one, because the partner's own naming is the thing
 * worth showing. Where there is not, the key read as words stands in.
 *
 * The raw key follows the name whenever the name cannot be recovered from it. That is the case that
 * matters: a display name is free to call the property anything, and a reader who cannot see which
 * registered key they are about to bind to has been shown a label instead of a reference. Where the
 * name is only the key capitalised, repeating it would be noise.
 */
export function eventPropertyDisplayName(property: ExternalEventProperty): string {
    const name = property.display_name || humanizeEventPropertyKey(property.key)
    if (!name) return property.key

    return asComparableKey(name) === asComparableKey(property.key) ? name : `${name} (${property.key})`
}

/**
 * The event type's registered properties as builder catalog entries.
 *
 * Ordinary fields in an `event.properties.*` namespace rather than a parallel builder: the property
 * map already carries the type information the builder maps to an operator set and an editor, so a
 * filter over an event reads and validates exactly as any other query does.
 *
 * Every one is filed under the event group, so a picker showing them cannot present them as
 * attributes of a subscriber — see {@link EVENT_PROPERTY_FIELD_GROUP}.
 */
export function buildEventPropertyFields(properties: ExternalEventProperty[] | undefined): PQueryBuilderField[] {
    if (!properties?.length) return []

    return properties.map((property) => ({
        key: eventPropertyPath(property.key),
        label: eventPropertyDisplayName(property),
        group: EVENT_PROPERTY_FIELD_GROUP,
        type: FIELD_TYPE_BY_PROPERTY_TYPE[property.type] ?? 'text',
        ...(isFractionalNumber(property) ? { fractional: true } : {}),
    }))
}
